import type { ReactNode, TableHTMLAttributes } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import ApprovalFormTable from '@/components/Approval/@components/ApprovalFormTable/ApprovalFormTable';
import { ApprovalTableCellInput } from '@/components/Approval/@components/ApprovalFormTable/styles';
import {
  formatProductionCostAmount as fmt,
  formatProductionCostMaturity,
  formatProductionCostPeriod,
  PRODUCTION_COST_BRAND_CODE,
  PRODUCTION_COST_CARRY_CODE,
  PRODUCTION_COST_CLAIM_CODE,
  PRODUCTION_COST_DEDUCT_CODES,
  PRODUCTION_COST_DIFF_LABELS,
  productionCostDiffAmounts,
  productionCostMaturityKeys,
  visibleProductionCostRows,
  type ProductionCostGroup,
  type ProductionCostSettlement,
} from '@/utils/approval/productionCost';
import {
  ProductionCostDescription,
  ProductionCostRoot,
  ProductionCostSection,
  ProductionCostTitle,
} from './styles';

type TableProps = TableHTMLAttributes<HTMLTableElement>;

type ProductionCostTablesProps = {
  /** YYYY-MM. 없으면 마감 기간 칸이 빔 */
  closingMonth: string | null;
  /** 생산비 마감 월 자리 — 작성은 월 선택, 조회는 글자 */
  monthSlot: ReactNode;
  /** null 이면 조회 전·조회 중·실패 — 표 머리글만 */
  settlement: ProductionCostSettlement | null;
  notes: Record<string, string>;
  /** 있으면 차이내역 내용이 입력칸 */
  onNoteChange?: (code: string, note: string) => void;
  /** 머리글 위 안내(로딩 등) — 표 사이에 끼움 */
  notice?: ReactNode;
  t: TFunction;
  lang: string;
  /** 저장 HTML 용 — 공통 표 대신 순수 table */
  plain?: boolean;
};

const PlainTable = (props: TableProps) => (
  <table className="approval-form-table" {...props} />
);

const GL_GROUPS: {
  key: 'payable' | 'tradePayable';
  label: string;
}[] = [
  { key: 'payable', label: '미지급금' },
  { key: 'tradePayable', label: '외상매입금' },
];

const GlGroupRows = ({
  label,
  group,
  t,
}: {
  label: string;
  group: ProductionCostGroup | null | undefined;
  t: TFunction;
}) => {
  const rows = visibleProductionCostRows(group);
  if (!rows.length) return null;
  return (
    <>
      {rows.map((row, i) => (
        <tr key={`${row.gtext}-${i}`}>
          {i === 0 && <td rowSpan={rows.length}>{t(label)}</td>}
          <td>{row.gtext}</td>
          <td>{row.waers}</td>
          <td className="align-right">{fmt(row.amount)}</td>
        </tr>
      ))}
      <tr className="row-summary">
        <td>{t('{{group}} 요약', { group: t(label) })}</td>
        <td />
        <td />
        <td className="align-right">{fmt(group?.subtotal)}</td>
      </tr>
    </>
  );
};

/**
 * 생산비 마감(216) 본문 — 레거시 작성·조회 화면의 표 4묶음.
 * 작성(입력칸)·조회(글자)·저장 HTML 이 같은 구성을 씀
 */
const ProductionCostTables = ({
  closingMonth,
  monthSlot,
  settlement,
  notes,
  onNoteChange,
  notice,
  t,
  lang,
  plain = false,
}: ProductionCostTablesProps) => {
  const Table = plain ? PlainTable : ApprovalFormTable;
  const Root = plain ? 'div' : ProductionCostRoot;
  const Section = plain ? 'div' : ProductionCostSection;
  const Title = plain ? 'h3' : ProductionCostTitle;
  const Description = plain ? 'p' : ProductionCostDescription;
  const productionCost = settlement?.productionCost;
  const financing = settlement?.externalFinancing;
  const difference = settlement?.difference;
  const maturityKeys = productionCostMaturityKeys(financing);
  const loanRows = financing?.rows ?? [];
  const diffAmounts = productionCostDiffAmounts(difference);

  const diffLabel = (code: string) =>
    code === PRODUCTION_COST_BRAND_CODE
      ? PRODUCTION_COST_DIFF_LABELS[code]
      : t(PRODUCTION_COST_DIFF_LABELS[code]);
  const noteCell = (code: string) => (
    <td className={onNoteChange ? 'align-left note-input' : 'align-left'}>
      {onNoteChange ? (
        <ApprovalTableCellInput
          value={notes[code] ?? ''}
          onChange={(e) => onNoteChange(code, e.target.value)}
          inputProps={{ 'aria-label': diffLabel(code) }}
        />
      ) : (
        notes[code] ?? ''
      )}
    </td>
  );
  const diffAmount = (code: string) => fmt(diffAmounts[code] ?? 0);

  return (
    <Root className="production-cost">
      <Section>
        <Title>{t('생산비 마감 월')}</Title>
        {monthSlot}
      </Section>
      {notice}

      <Section>
        <Title>{t('생산비 발생금액')}</Title>
        <Description className="description">
          {t('원화 생산/미지급 발생 리스트')}
        </Description>
        <Description>
          {t('생산비 마감 기간')} :{' '}
          {closingMonth ? formatProductionCostPeriod(closingMonth) : ''}
        </Description>
        <Table>
          <colgroup>
            <col />
            <col />
            <col className="col-currency" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>{t('G/L계정명')}</th>
              <th>{t('사업영역명')}</th>
              <th>{t('통화키')}</th>
              <th>{t('합계 : 금액', { nsSeparator: false })}</th>
            </tr>
          </thead>
          {productionCost && (
            <tbody>
              {GL_GROUPS.map(({ key, label }) => (
                <GlGroupRows
                  key={key}
                  label={label}
                  group={productionCost[key]}
                  t={t}
                />
              ))}
              <tr className="row-total">
                <td>{t('총합계')}</td>
                <td />
                <td />
                <td className="align-right">{fmt(productionCost.total)}</td>
              </tr>
            </tbody>
          )}
        </Table>
      </Section>

      <Section>
        <Title>{t('외담대 발행일정')}</Title>
        <Description>
          {t('외담대 발행일자')} : {(financing?.issuanceDates ?? []).join(', ')}
        </Description>
      </Section>

      <Section>
        <Title>{t('외담대 발행금액')}</Title>
        <Description className="description">
          {t('원화 생산/미지급 외담대 발행 리스트')}
        </Description>
        <Table>
          <thead>
            <tr>
              <th rowSpan={maturityKeys.length ? 2 : 1}>{t('발행여부')}</th>
              <th rowSpan={maturityKeys.length ? 2 : 1}>
                {t('내용', { context: 'detail' })}
              </th>
              <th colSpan={maturityKeys.length || 1}>{t('만기일')}</th>
              <th rowSpan={maturityKeys.length ? 2 : 1}>{t('합계')}</th>
            </tr>
            {maturityKeys.length > 0 && (
              <tr>
                {maturityKeys.map((key) => (
                  <th key={key}>{formatProductionCostMaturity(key, lang)}</th>
                ))}
              </tr>
            )}
          </thead>
          {financing && (
            <tbody>
              {loanRows.map((row, i) => (
                <tr key={`${row?.content}-${i}`}>
                  {i === 0 && (
                    <td rowSpan={loanRows.length}>{t('외담대발행')}</td>
                  )}
                  <td>{row?.content}</td>
                  {maturityKeys.map((key) => (
                    <td key={key} className="align-right">
                      {fmt(row?.amounts?.[key])}
                    </td>
                  ))}
                  <td className="align-right">{fmt(row?.rowTotal)}</td>
                </tr>
              ))}
              <tr className="row-total">
                <td>{t('합계')}</td>
                <td />
                {maturityKeys.map((key) => (
                  <td key={key} className="align-right">
                    {fmt(financing.columnTotals?.[key])}
                  </td>
                ))}
                <td className="align-right">{fmt(financing.total)}</td>
              </tr>
            </tbody>
          )}
        </Table>
      </Section>

      <Section>
        <Title>
          {t('생산비 발생금액 - 외담대 발행금액간 차이내역 (원화)')}
        </Title>
        <Description>
          {t('차이금액')} :{' '}
          {difference
            ? t('{{amount}}원', {
                amount: fmt(Math.abs(Number(difference.diffAmount) || 0)),
              })
            : ''}
        </Description>
        <Table>
          <thead>
            <tr>
              <th colSpan={2}>{t('구분')}</th>
              <th>{t('금액')}</th>
              <th>{t('내용', { context: 'detail' })}</th>
            </tr>
          </thead>
          {difference && (
            <tbody>
              <tr>
                <td colSpan={2}>{diffLabel(PRODUCTION_COST_CLAIM_CODE)}</td>
                <td className="align-right">
                  {diffAmount(PRODUCTION_COST_CLAIM_CODE)}
                </td>
                {noteCell(PRODUCTION_COST_CLAIM_CODE)}
              </tr>
              {PRODUCTION_COST_DEDUCT_CODES.map((code, i) => (
                <tr key={code}>
                  {i === 0 && (
                    <td rowSpan={PRODUCTION_COST_DEDUCT_CODES.length}>
                      {t('공제내역')}
                    </td>
                  )}
                  <td>{diffLabel(code)}</td>
                  <td className="align-right">{diffAmount(code)}</td>
                  {noteCell(code)}
                </tr>
              ))}
              <tr>
                <td>{t('이월내역')}</td>
                <td>{diffLabel(PRODUCTION_COST_CARRY_CODE)}</td>
                <td className="align-right">
                  {diffAmount(PRODUCTION_COST_CARRY_CODE)}
                </td>
                {noteCell(PRODUCTION_COST_CARRY_CODE)}
              </tr>
              <tr className="row-total">
                <td>{t('합계')}</td>
                <td />
                <td className="align-right">
                  {fmt(Math.abs(Number(difference.total) || 0))}
                </td>
                <td />
              </tr>
            </tbody>
          )}
        </Table>
      </Section>
    </Root>
  );
};

export default ProductionCostTables;

/** attributes.content — 조회 화면은 data 로 다시 그리므로 검색·보관용 스냅샷 */
export const buildProductionCostContentHtml = ({
  closingMonth,
  settlement,
  notes,
  t,
  lang,
}: Pick<
  ProductionCostTablesProps,
  'closingMonth' | 'settlement' | 'notes' | 't' | 'lang'
>): string =>
  renderToStaticMarkup(
    <ProductionCostTables
      closingMonth={closingMonth}
      monthSlot={<div>{closingMonth ?? ''}</div>}
      settlement={settlement}
      notes={notes}
      t={t}
      lang={lang}
      plain
    />,
  );
